import { useState } from 'react';
import useSWR, { mutate } from 'swr';
import { toast } from 'sonner';
import { get, post, ApiError } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DetailDrawer } from '@/components/domain/DetailDrawer';
import { ApproveStoreDialog } from '@/components/domain/ApproveStoreDialog';
import { formatRelative } from '@/lib/time';
import { Thumbnail } from '@/components/domain/Thumbnail';

type Item = {
  id: string;
  fundraiserName?: string;
  schoolName?: string;
  adminName?: string;
  storeNumberDisplay?: string;
  endDate?: string;
  submittedAt?: string;
};
type Payload = { items: Item[] };
type DetailPayload = Item & Record<string, unknown>;

type ApprovalError = { message: string; canRetry: boolean };

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="rounded-md border border-danger/30 bg-danger/5 p-3 text-[12px] text-danger">
      <p className="font-medium">Approval failed</p>
      <p className="mt-1 text-text">{message}</p>
      {onRetry ? (
        <Button size="sm" variant="secondary" className="mt-2" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export default function PendingApprovals() {
  const [openId, setOpenId] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approvalError, setApprovalError] = useState<ApprovalError | null>(null);

  const { data, isLoading } = useSWR<Payload>('/api/pending-approvals', get);
  const { data: detail, isLoading: detailLoading } = useSWR<DetailPayload>(
    openId ? `/api/active-stores/${openId}` : null, get
  );

  const filtered = (data?.items || []).filter((it) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(it.fundraiserName || '').toLowerCase().includes(needle) ||
      String(it.schoolName || '').toLowerCase().includes(needle) ||
      String(it.adminName || '').toLowerCase().includes(needle) ||
      String(it.storeNumberDisplay || '').toLowerCase().includes(needle)
    );
  });

  function closeDrawer() {
    setOpenId(null);
    setApprovalError(null);
    setConfirmOpen(false);
  }

  async function handleApprove() {
    if (!openId) return;
    setApproving(true);
    setApprovalError(null);
    try {
      const result = await post<{ productsCreated: number }>(`/api/team-stores/${openId}/approve`, {});
      setConfirmOpen(false);
      setOpenId(null);
      toast.success(`Approved · ${result.productsCreated} products created`);
      await mutate('/api/pending-approvals');
    } catch (e) {
      setConfirmOpen(false);
      const body = (e as ApiError).body as { message?: string; error?: string } | null;
      if (body?.error === 'product_creation_failed') {
        setApprovalError({
          message: `Approval committed but product creation failed: ${body.message || 'unknown'}. Open the store from Active Stores and use Catalog → Add missing (never /create on a live store).`,
          canRetry: false,
        });
      } else if (body?.error === 'not_found') {
        setApprovalError({ message: 'Store no longer exists.', canRetry: false });
      } else {
        setApprovalError({
          message: body?.message || 'Approval failed.',
          canRetry: body?.error === 'server_error',
        });
      }
    } finally {
      setApproving(false);
    }
  }

  const topActions = approvalError ? (
    <ErrorBanner
      message={approvalError.message}
      onRetry={approvalError.canRetry ? handleApprove : undefined}
    />
  ) : (
    <Button onClick={() => setConfirmOpen(true)}>Approve &amp; Create Products</Button>
  );

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Pending Approvals</h1>
      <p className="mb-3 text-[12px] text-muted">
        Team stores awaiting rep approval · {filtered.length}{q ? ` of ${data?.items.length ?? 0}` : ''} shown
      </p>

      {data?.items.length ? (
        <div className="mb-3 flex max-w-[420px]">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by fundraiser, school, admin, store #…" className="flex-1" />
        </div>
      ) : null}

      <div className="rounded-md bg-surface shadow-card">
        {isLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : filtered.length ? (
          filtered.map((it) => (
            <button
              key={it.id}
              onClick={() => setOpenId(it.id)}
              className="flex w-full items-center gap-3 border-b border-divider px-3 py-2 text-left hover:bg-bg/60 last:border-b-0"
            >
              <Thumbnail path={(it as { logos?: { gcsPath?: string }[]; logoPath?: string }).logos?.find((l) => l?.gcsPath)?.gcsPath || (it as { logoPath?: string }).logoPath} alt="store logo" className="h-8 w-8" />
              <span className="font-mono text-[10px] text-subtle w-20 shrink-0">
                {it.storeNumberDisplay || it.id.slice(0, 12)}
              </span>
              <span className="flex-1 truncate text-[12px]">
                {it.fundraiserName || 'Unknown'}
                {it.adminName && <span className="ml-2 text-muted">— {it.adminName}</span>}
              </span>
              <Badge className="bg-warning/10 text-warning">Awaiting Approval</Badge>
              <span className="w-12 shrink-0 text-right text-[11px] text-muted">
                {it.submittedAt ? formatRelative(it.submittedAt) : ''}
              </span>
            </button>
          ))
        ) : data?.items.length ? (
          <div className="p-8 text-center text-[12px] text-muted">No pending approvals match "{q}".</div>
        ) : (
          <div className="p-8 text-center text-[12px] text-muted">Nothing waiting on you. ✓</div>
        )}
      </div>

      <DetailDrawer
        open={!!openId}
        onClose={closeDrawer}
        title={detail?.schoolName || detail?.fundraiserName || 'Pending Approval'}
        subtitle={detail?.storeNumberDisplay || openId || undefined}
        loading={detailLoading}
        data={detail}
        thumbnailFields={['logoPath']}
        topActions={openId ? topActions : null}
      />

      <ApproveStoreDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        store={{
          fundraiserName: detail?.fundraiserName,
          schoolName: detail?.schoolName,
          storeNumberDisplay: detail?.storeNumberDisplay,
        }}
        busy={approving}
        onConfirm={handleApprove}
      />
    </div>
  );
}
